import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, ArrowLeft, Send, Trash2, Video, Shirt, Camera,
  User, Bot, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STARTER_PROMPTS = [
  { text: "How do I build a film industry portfolio?", icon: Video, context: "film" },
  { text: "What certifications help in fashion design?", icon: Shirt, context: "fashion" },
  { text: "How can I monetize my content creation?", icon: Camera, context: "career" },
  { text: "What's the best path to becoming a cinematographer?", icon: Video, context: "film" },
  { text: "How do I negotiate my first paid gig?", icon: Sparkles, context: "career" }
];

export default function CreativeCoach() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText, context = 'general') => {
    if (!messageText.trim() || !user) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/studio/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: messageText,
          context: context,
          session_id: sessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to get response');
        setMessages(prev => prev.slice(0, -1)); // Remove the user message
      }
    } catch (err) {
      toast.error('Failed to connect to AI Coach');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="creative-coach">
        <div className="container mx-auto px-4 py-20 text-center">
          <Sparkles className="h-16 w-16 text-violet-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">AI Creative Coach</h1>
          <p className="text-violet-200 mb-8 max-w-md mx-auto">
            Get personalized career guidance for film, fashion, and content creation. 
            Log in to start your conversation.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-violet-600 hover:bg-violet-700">
              Log In to Chat
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="creative-coach">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/legacystudio" className="text-violet-300 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Creative Coach</h1>
                  <p className="text-violet-300 text-sm">AI-Powered Career Guidance</p>
                </div>
              </div>
            </div>
            {messages.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearChat}
                className="text-violet-300 hover:text-white hover:bg-white/10"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Chat Area */}
        <div className="min-h-[60vh] flex flex-col">
          {messages.length === 0 ? (
            // Welcome Screen
            <div className="flex-1 flex flex-col items-center justify-center py-12">
              <div className="p-4 bg-gradient-to-br from-violet-500/20 to-purple-600/20 rounded-2xl mb-6">
                <Sparkles className="h-12 w-12 text-violet-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Creative Coach</h2>
              <p className="text-violet-200 text-center max-w-md mb-8">
                Your AI-powered career advisor for film, fashion, and content creation. 
                Ask me anything about building your creative career.
              </p>
              
              <div className="w-full max-w-2xl">
                <p className="text-violet-400 text-sm mb-3 text-center">Try asking:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {STARTER_PROMPTS.map((prompt, idx) => {
                    const Icon = prompt.icon;
                    return (
                      <motion.button
                        key={`item-${idx}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        onClick={() => sendMessage(prompt.text, prompt.context)}
                        className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl text-left hover:bg-violet-500/20 hover:border-violet-400/50 transition-all group"
                      >
                        <Icon className="h-5 w-5 text-violet-400 group-hover:text-violet-300" />
                        <span className="text-violet-100 text-sm">{prompt.text}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            // Messages
            <div className="flex-1 space-y-4 mb-6">
              <AnimatePresence>
                {messages.map((msg, idx) => (
                  <motion.div
                    key={`item-${idx}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg h-fit">
                        <Bot className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] p-4 rounded-2xl ${
                      msg.role === 'user' 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-white/10 text-violet-100'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="p-2 bg-violet-500/30 rounded-lg h-fit">
                        <User className="h-5 w-5 text-violet-300" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg h-fit">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div className="bg-white/10 p-4 rounded-2xl">
                    <Loader2 className="h-5 w-5 text-violet-400 animate-spin" />
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="sticky bottom-6">
            <div className="flex gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Ask about your creative career..."
                className="flex-1 bg-transparent border-0 text-white placeholder:text-violet-300/50 resize-none min-h-[50px] max-h-[150px] focus:ring-0"
                rows={1}
              />
              <Button 
                type="submit" 
                disabled={!input.trim() || loading}
                className="bg-violet-600 hover:bg-violet-700 self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-center text-violet-400/60 text-xs mt-2">
              Creative Coach provides guidance based on industry knowledge. Always verify advice with professionals.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
